import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { db } from './firebase';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { playCardFlip, playCardDraw, playCardDiscard, playCardSwap, playPeek, playRoundWin, playGameWin, playRoundLose, resumeAudio } from './sounds';

const SUITS = ['♠', '♥', '♦', '♣'];
const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const POINTS = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 10, Q: 10, K: 0 };
const RED_SUITS = ['♥', '♦'];
const ASSUMED_FACEDOWN_VALUE = 5;

const BOT_NAMES = [
  'Alex (Bot)', 'Jordan (Bot)', 'Taylor (Bot)', 'Morgan (Bot)',
  'Casey (Bot)', 'Riley (Bot)', 'Avery (Bot)', 'Quinn (Bot)',
  'Blake (Bot)', 'Sage (Bot)', 'Drew (Bot)', 'Jamie (Bot)',
];

function buildDeck() {
  let d = [];
  for (let s of SUITS)
    for (let v of VALUES)
      d.push({ suit: s, value: v, points: POINTS[v], id: Math.random().toString(36).slice(2) });
  for (let i = d.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

function isRed(card) { return RED_SUITS.includes(card.suit); }

function calcScore(cards) {
  const counts = {};
  cards.forEach(c => { counts[c.value] = (counts[c.value] || 0) + 1; });
  let total = 0;
  const processed = {};
  cards.forEach(card => {
    if (!processed[card.value]) {
      processed[card.value] = true;
      const leftover = counts[card.value] % 2;
      total += leftover * card.points;
    }
  });
  return total;
}

function getCardCode(card) {
  const valueMap = { 'A': 'A', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '10': '0', 'J': 'J', 'Q': 'Q', 'K': 'K' };
  const suitMap = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' };
  return `${valueMap[card.value]}${suitMap[card.suit]}`;
}

const CARD_BACK_STYLES = {
  classic:  { background: '#1D4E89', border: '2px solid #0C447C' },
  neon:     { background: 'linear-gradient(135deg,#7F77DD,#D85A30)' },
  midnight: { background: '#1a1a2e', border: '2px solid #533AB7' },
  vintage:  { background: '#8B4513', border: '2px solid #6B3410' },
  luxury:   { background: 'linear-gradient(135deg,#B8860B,#FFD700)' },
  ocean:    { background: 'linear-gradient(135deg,#006994,#00CED1)' },
};

const TABLE_STYLES = {
  felt:          { background: '#1D6B3A' },
  'green-felt':  { background: '#1D6B3A' },
  'tb-midnight': { background: '#1a1a2e' },
  'tb-beach':    { background: '#F4A460' },
  'tb-wood':     { background: '#8B4513' },
  'tb-marble':   { background: '#E8E8E8' },
  'tb-neon':     { background: '#0D0D0D' },
};

const S = {
  btn: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  btnSm: { background: 'none', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#fff', cursor: 'pointer' },
  btnWhite: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, color: '#fff', cursor: 'pointer' },
  btnOutline: { background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '1.5rem' },
};

function CardBack({ skinId = 'classic', size = 'normal', glow = false, onClick }) {
  const style = CARD_BACK_STYLES[skinId] || CARD_BACK_STYLES.classic;
  const w = size === 'large' ? 65 : 52;
const h = size === 'large' ? 93 : 74;
  return (
    <div onClick={onClick} style={{
      width: w, height: h, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 22, color: 'rgba(255,255,255,0.3)',
      cursor: onClick ? 'pointer' : 'default',
      outline: glow ? '3px solid #1D9E75' : 'none', outlineOffset: 2,
      ...style
    }}>♦</div>
  );
}

function CardFace({ card, size = 'normal', highlight = false, onClick }) {
  const w = size === 'large' ? 65 : 52;
const h = size === 'large' ? 93 : 74;
  const code = getCardCode(card);
  return (
    <div onClick={onClick} style={{
      width: w, height: h, borderRadius: 6, background: '#fff',
      border: highlight ? '2px solid #1D9E75' : '1.5px solid #e0e0e0',
      cursor: onClick ? 'pointer' : 'default',
      overflow: 'hidden',
      boxShadow: highlight ? '0 0 0 2px #1D9E75' : 'none',
    }}>
      <img
        src={`https://deckofcardsapi.com/static/img/${code}.png`}
        alt={`${card.value}${card.suit}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
    </div>
  );
}

export default function Game({ onOpenShop, onOpenLeaderboard, onGameStateChange, onOpenOnline, onOpenHowToPlay, onSignOut }) {
  const { user, profile } = useAuth();
  const skinId = profile?.cosmetics?.activeSkin || 'classic';
  const tableId = profile?.cosmetics?.activeTable || 'felt';
  const tableStyle = TABLE_STYLES[tableId] || TABLE_STYLES.felt;

  const [phase, setPhase] = useState('setup');
  const [showStats, setShowStats] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [gameEnding, setGameEnding] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [players, setPlayers] = useState([]);
  const [playerNames, setPlayerNames] = useState(() => {
  const name = profile?.displayName || user?.displayName || '';
  return [name, '', '', ''];
});
  const [playerCount, setPlayerCount] = useState(2);
  const [maxScore, setMaxScore] = useState(100);
  const [deck, setDeck] = useState([]);
  const [discard, setDiscard] = useState([]);
  const [drawnCard, setDrawnCard] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [turnPhase, setTurnPhase] = useState('draw');
  const [round, setRound] = useState(1);
  const [roundResults, setRoundResults] = useState([]);
  const [peekingCards, setPeekingCards] = useState(new Set());

  const playersRef = useRef([]);
  const deckRef = useRef([]);
  const discardRef = useRef([]);
  const drawnCardRef = useRef(null);
  const currentPlayerRef = useRef(0);
  const turnPhaseRef = useRef('draw');
  const phaseRef = useRef('setup');
  const maxScoreRef = useRef(100);
  const gameEndingRef = useRef(false);

  useEffect(() => { playersRef.current = players; }, [players]);
  useEffect(() => { deckRef.current = deck; }, [deck]);
  useEffect(() => { discardRef.current = discard; }, [discard]);
  useEffect(() => { drawnCardRef.current = drawnCard; }, [drawnCard]);
  useEffect(() => { currentPlayerRef.current = currentPlayer; }, [currentPlayer]);
  useEffect(() => { turnPhaseRef.current = turnPhase; }, [turnPhase]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { maxScoreRef.current = maxScore; }, [maxScore]);
  useEffect(() => { gameEndingRef.current = gameEnding; }, [gameEnding]);
  useEffect(() => {
  if (profile?.displayName || user?.displayName) {
    setPlayerNames(prev => {
      const n = [...prev];
      if (!n[0]) n[0] = profile?.displayName || user?.displayName || '';
      return n;
    });
  }
}, [profile, user]);

  const cpuTimer = useRef(null);

  const finishRound = useCallback((finalPlayers, maxSc) => {
    const scores = finalPlayers.map(p => ({ name: p.name, roundScore: calcScore(p.cards) }));
    const min = Math.min(...scores.map(s => s.roundScore));
    const updated = finalPlayers.map((p, i) => ({ ...p, score: p.score + scores[i].roundScore }));
    setPlayers(updated);
    setRoundResults(scores.map((s, i) => ({
      ...s, newTotal: updated[i].score, isWinner: s.roundScore === min,
      eliminated: updated[i].score >= maxSc,
    })));
    setPhase('roundover');
    // Track guest game count
const isGameOver = updated.some(p => p.score >= maxSc);
if (user?.isAnonymous && isGameOver) {
  const guestGames = parseInt(localStorage.getItem('guestGames') || '0') + 1;
  localStorage.setItem('guestGames', guestGames);
}
    if (user) {
      const humanPlayer = finalPlayers.find(p => !p.isCPU);
      if (humanPlayer) {
        const humanScore = calcScore(humanPlayer.cards);
        const humanWonRound = humanScore === min;
        const isGameOver = updated.some(p => p.score >= maxSc);
        const sortedByScore = [...updated].sort((a, b) => a.score - b.score);
        const humanWonGame = sortedByScore[0] && !sortedByScore[0].isCPU;
        const hasHumanOpponents = finalPlayers.filter(p => !p.isCPU).length > 1;
        const roundUpdates = {
          'stats.totalRounds': increment(1),
          'stats.roundsWon': increment(humanWonRound ? 1 : 0),
        };
        const currentBest = humanPlayer.stats?.bestRoundScore;
        if (currentBest === null || currentBest === undefined || humanScore < currentBest) {
          roundUpdates['stats.bestRoundScore'] = humanScore;
        }
        if (isGameOver) {
          roundUpdates['stats.gamesPlayed'] = increment(1);
          if (hasHumanOpponents) {
            roundUpdates['stats.wins'] = increment(humanWonGame ? 1 : 0);
          } else {
            roundUpdates['stats.botGamesPlayed'] = increment(1);
            roundUpdates['stats.botWins'] = increment(humanWonGame ? 1 : 0);
          }
        }
        updateDoc(doc(db, 'users', user.uid), roundUpdates).catch(() => {});
      }
    }
  }, [user]);

  const doAdvancePlayer = useCallback((newPlayers, newDeck, newDiscard, maxSc) => {
    const allFaceUp = newPlayers.every(p => p.cards.every(c => c.faceUp));
    if (allFaceUp) {
      gameEndingRef.current = true;
      setGameEnding(true);
      setPlayers(newPlayers); playersRef.current = newPlayers;
      if (newDeck !== undefined) { setDeck(newDeck); deckRef.current = newDeck; }
      if (newDiscard !== undefined) { setDiscard(newDiscard); discardRef.current = newDiscard; }
      setDrawnCard(null); drawnCardRef.current = null;
      setTurnPhase('draw'); turnPhaseRef.current = 'draw';
      setTimeout(() => {
        gameEndingRef.current = false;
        setGameEnding(false);
        finishRound(newPlayers, maxSc);
      }, 2000);
      return;
    }
    const currentIdx = currentPlayerRef.current;
    const total = newPlayers.length;
    let nextPlayer = (currentIdx + 1) % total;
    for (let i = 1; i < total; i++) {
      const idx = (currentIdx + i) % total;
      if (newPlayers[idx].cards.some(c => !c.faceUp)) { nextPlayer = idx; break; }
    }
    setPlayers(newPlayers); playersRef.current = newPlayers;
    if (newDeck !== undefined) { setDeck(newDeck); deckRef.current = newDeck; }
    if (newDiscard !== undefined) { setDiscard(newDiscard); discardRef.current = newDiscard; }
    setDrawnCard(null); drawnCardRef.current = null;
    setCurrentPlayer(nextPlayer); currentPlayerRef.current = nextPlayer;
    setTurnPhase('draw'); turnPhaseRef.current = 'draw';
  }, [finishRound]);

  const startGame = () => {
  resumeAudio();
    const active = playerNames.slice(0, playerCount)
      .map(n => n?.trim() || '')
      .filter(n => n !== '')
      .map(n => ({ name: n, score: 0, cards: [], isCPU: n.includes('(Bot)') }));
    if (active.length < 2) { setErrorMsg('Add at least 2 players to start the game.'); return; }
    setPlayers(active);
    setRound(1);
    dealRound(active, buildDeck());
  };

  const dealRound = (ps, freshDeck) => {
    const deckCopy = [...freshDeck];
    const dealt = ps.map(p => ({
      ...p,
      cards: Array.from({ length: 4 }, (_, i) => ({ ...deckCopy.shift(), faceUp: false, position: i }))
    }));
    playersRef.current = dealt;
    deckRef.current = deckCopy;
    discardRef.current = [];
    drawnCardRef.current = null;
    const startingPlayer = (round - 1) % ps.length;
    setCurrentPlayer(startingPlayer);
    turnPhaseRef.current = 'draw';
    phaseRef.current = 'playing';
    gameEndingRef.current = false;
    setPlayers(dealt);
    setDeck(deckCopy);
    setDiscard([]);
    setDrawnCard(null);
    setCurrentPlayer(0);
    setTurnPhase('draw');
    setGameEnding(false);
    setPeekingCards(new Set());
    setPhase('playing');
    onGameStateChange && onGameStateChange(true);
  };

  const drawFromDeck = () => {
    if (gameEndingRef.current) return;
    if (phaseRef.current !== 'playing') return;
    if (turnPhaseRef.current !== 'draw') return;
    if (deckRef.current.length === 0) return;
    const newDeck = [...deckRef.current];
    const card = newDeck.shift();
    deckRef.current = newDeck;
    setDeck(newDeck);
    drawnCardRef.current = card;
    setDrawnCard(card);
    turnPhaseRef.current = 'action';
    setTurnPhase('action');
  };

  const takeDiscard = () => {
    if (gameEndingRef.current) return;
    if (phaseRef.current !== 'playing') return;
    if (turnPhaseRef.current !== 'draw') return;
    if (discardRef.current.length === 0) return;
    const newDiscard = [...discardRef.current];
    const card = newDiscard.pop();
    discardRef.current = newDiscard;
    setDiscard(newDiscard);
    drawnCardRef.current = card;
    setDrawnCard(card);
    turnPhaseRef.current = 'swap';
    setTurnPhase('swap');
  };

  const flipCardDiscardDrawn = (pi, ci) => {
    if (gameEndingRef.current) return;
    const players = playersRef.current;
    const currentPlayer = currentPlayerRef.current;
    const drawnCard = drawnCardRef.current;
    const deck = deckRef.current;
    const discard = discardRef.current;
    const maxSc = maxScoreRef.current;
    if (pi !== currentPlayer || turnPhaseRef.current !== 'action' || !drawnCard) return;
    if (players[pi].cards[ci].faceUp) return;
    const newPlayers = players.map((pl, pli) =>
      pli !== pi ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...c, faceUp: true } : c) }
    );
    doAdvancePlayer(newPlayers, deck, [...discard, drawnCard], maxSc);
  };

  const swapDrawnIntoCard = (pi, ci) => {
    if (gameEndingRef.current) return;
    const players = playersRef.current;
    const currentPlayer = currentPlayerRef.current;
    const drawnCard = drawnCardRef.current;
    const deck = deckRef.current;
    const discard = discardRef.current;
    const maxSc = maxScoreRef.current;
    if (pi !== currentPlayer || turnPhaseRef.current !== 'swap' || !drawnCard) return;
    if (players[pi].cards[ci].faceUp) return;
    const old = players[pi].cards[ci];
    const newPlayers = players.map((pl, pli) =>
      pli !== pi ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...drawnCard, faceUp: true, position: cii } : c) }
    );
    doAdvancePlayer(newPlayers, deck, [...discard, old], maxSc);
  };

  const flipBlindCard = (pi, ci) => {
    if (gameEndingRef.current) return;
    const players = playersRef.current;
    const currentPlayer = currentPlayerRef.current;
    const deck = deckRef.current;
    const discard = discardRef.current;
    const maxSc = maxScoreRef.current;
    if (pi !== currentPlayer || turnPhaseRef.current !== 'flip') return;
    if (players[pi].cards[ci].faceUp) return;
    const newPlayers = players.map((pl, pli) =>
      pli !== pi ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...c, faceUp: true } : c) }
    );
    doAdvancePlayer(newPlayers, deck, discard, maxSc);
  };

  const discardDrawn = () => {
    if (gameEndingRef.current) return;
    const players = playersRef.current;
    const currentPlayer = currentPlayerRef.current;
    const drawnCard = drawnCardRef.current;
    const deck = deckRef.current;
    const discard = discardRef.current;
    const maxSc = maxScoreRef.current;
    if (!drawnCard) return;
    const hasFaceDown = players[currentPlayer]?.cards.some(c => !c.faceUp);
    const newDiscard = [...discard, drawnCard];
    discardRef.current = newDiscard;
    setDiscard(newDiscard);
    drawnCardRef.current = null;
    setDrawnCard(null);
    if (hasFaceDown) {
      turnPhaseRef.current = 'flip';
      setTurnPhase('flip');
    } else {
      doAdvancePlayer(players, deck, newDiscard, maxSc);
    }
  };

  const nextRound = () => {
    if (players.some(p => p.score >= maxScore)) { setPhase('gameover'); return; }
    setGameEnding(false);
    gameEndingRef.current = false;
    setRound(r => r + 1);
    dealRound(players, buildDeck());
  };

  // ── CPU AI ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (cpuTimer.current) clearTimeout(cpuTimer.current);
    if (phaseRef.current !== 'playing') return;
    if (gameEndingRef.current) return;
    const cp = playersRef.current[currentPlayerRef.current];
    if (!cp || !cp.isCPU) return;

    cpuTimer.current = setTimeout(() => {
      if (phaseRef.current !== 'playing') return;
      if (gameEndingRef.current) return;
      const players = playersRef.current;
      const currentPlayer = currentPlayerRef.current;
      const turnPhase = turnPhaseRef.current;
      const drawnCard = drawnCardRef.current;
      const deck = deckRef.current;
      const discard = discardRef.current;
      const maxSc = maxScoreRef.current;
      const cp = players[currentPlayer];
      if (!cp || !cp.isCPU) return;

      const knownCards = cp.cards.map((c, i) => ({ ...c, idx: i })).filter((c, i) => c.faceUp || i >= 2);
      const faceUpCards = knownCards;
      const faceDownCards = cp.cards.map((c, i) => ({ ...c, idx: i })).filter(c => !c.faceUp);
      const faceUpCounts = {};
      faceUpCards.forEach(c => { faceUpCounts[c.value] = (faceUpCounts[c.value] || 0) + 1; });
      const worstFaceUp = faceUpCards.reduce((w, c) => (!w || c.points > w.points) ? c : w, null);
      const worstNonPaired = faceUpCards
        .filter(c => faceUpCounts[c.value] === 1 && c.points > 0)
        .reduce((w, c) => (!w || c.points > w.points) ? c : w, null);

    if (turnPhase === 'draw') {
        const topDiscard = discard[discard.length - 1];
        const matchesExisting = topDiscard && faceUpCards.some(c => c.value === topDiscard.value && faceUpCounts[c.value] === 1);
        // Only take discard if CPU has a face-down card to swap it into
        const hasFaceDownToSwap = faceDownCards.length > 0;
        const shouldTakeDiscard = topDiscard && hasFaceDownToSwap && (
          topDiscard.points === 0 ||
          matchesExisting ||
          (topDiscard.points <= 2 && faceDownCards.length > 0)
        );
        if (shouldTakeDiscard) {
          const newDiscard = [...discard];
          const card = newDiscard.pop();
          discardRef.current = newDiscard;
          setDiscard(newDiscard);
          drawnCardRef.current = card;
          setDrawnCard(card);
          turnPhaseRef.current = 'swap';
          setTurnPhase('swap');
        } else if (deck.length > 0) {
          const newDeck = [...deck];
          const card = newDeck.shift();
          deckRef.current = newDeck;
          setDeck(newDeck);
          drawnCardRef.current = card;
          setDrawnCard(card);
          turnPhaseRef.current = 'action';
          setTurnPhase('action');
        }
        return;
      }

      if (turnPhase === 'action' && drawnCard) {
        const matchingFaceUp = faceUpCards.filter(c => c.value === drawnCard.value);
        const existingKingPair = faceUpCounts['K'] === 2;
        const shouldDiscardKing = drawnCard.points === 0 && matchingFaceUp.length === 1 && matchingFaceUp[0].points > 0 && !existingKingPair;

       if (drawnCard.points === 0 && !shouldDiscardKing) {
          if (faceDownCards.length > 0) {
            const target = faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
            const newPlayers = players.map((pl, pli) =>
              pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
            );
            doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[target.idx]], maxSc);
          } else {
            doAdvancePlayer(players, deck, [...discard, drawnCard], maxSc);
          }
        } else if (matchingFaceUp.length === 1 && faceDownCards.length > 0) {
          const target = faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
          const newPlayers = players.map((pl, pli) =>
            pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
          );
          doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[target.idx]], maxSc);
        } else {
          const worstKnownCard = faceUpCards.reduce((w, c) => (!w || c.points > w.points) ? c : w, null);
          const blindFaceDown = cp.cards.map((c, i) => ({ ...c, idx: i })).filter((c, i) => !c.faceUp && i < 2);
          const worstAssumed = blindFaceDown.length > 0 ? ASSUMED_FACEDOWN_VALUE : 0;
          const worstKnownPoints = worstKnownCard ? worstKnownCard.points : 0;
          const worstPoints = Math.max(worstKnownPoints, worstAssumed);
          const isHighCard = drawnCard.points >= 4;
          const hasKnownMatch = faceUpCards.some(c => c.value === drawnCard.value);
          const allVisibleHigherThanDrawn = faceUpCards.length > 0 && faceUpCards.every(c => c.points > drawnCard.points);
          const shouldSwapBlind = blindFaceDown.length > 0 && (
            drawnCard.points <= 3 ||
            (drawnCard.points < ASSUMED_FACEDOWN_VALUE && allVisibleHigherThanDrawn)
          );

          if (drawnCard.points < worstPoints && (!isHighCard || hasKnownMatch)) {
            if (worstKnownCard && !worstKnownCard.faceUp && drawnCard.points < worstKnownCard.points) {
              const newPlayers = players.map((pl, pli) =>
                pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === worstKnownCard.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
              );
              doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[worstKnownCard.idx]], maxSc);
            } else if (faceDownCards.length > 0) {
              const target = faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
              const newPlayers = players.map((pl, pli) =>
                pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
              );
              doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[target.idx]], maxSc);
            } else {
              doAdvancePlayer(players, deck, [...discard, drawnCard], maxSc);
            }
          } else if (shouldSwapBlind) {
            // Drawn card is better than assumed blind card value — swap it in
            const target = blindFaceDown[Math.floor(Math.random() * blindFaceDown.length)];
            const newPlayers = players.map((pl, pli) =>
              pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
            );
            doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[target.idx]], maxSc);
          } else if (faceDownCards.length > 0) {
            // Discard drawn card and flip a blind card to reveal it
            const target = blindFaceDown.length > 0
              ? blindFaceDown[Math.floor(Math.random() * blindFaceDown.length)]
              : faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
            const newPlayers = players.map((pl, pli) =>
              pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...c, faceUp: true } : c) }
            );
            doAdvancePlayer(newPlayers, deck, [...discard, drawnCard], maxSc);
          } else {
            doAdvancePlayer(players, deck, [...discard, drawnCard], maxSc);
          }
        }
        return;
      }

      if (turnPhase === 'swap' && drawnCard) {
        const isHighCard = drawnCard.points >= 4;
        const hasKnownMatch = faceUpCards.some(c => c.value === drawnCard.value);
        if (faceDownCards.length > 0 && (!isHighCard || hasKnownMatch)) {
          const target = faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
          const newPlayers = players.map((pl, pli) =>
            pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...drawnCard, faceUp: true, position: cii } : c) }
          );
          doAdvancePlayer(newPlayers, deck, [...discard, cp.cards[target.idx]], maxSc);
        } else if (faceDownCards.length > 0) {
          const target = faceDownCards[Math.floor(Math.random() * faceDownCards.length)];
          const newPlayers = players.map((pl, pli) =>
            pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...c, faceUp: true } : c) }
          );
          doAdvancePlayer(newPlayers, deck, [...discard, drawnCard], maxSc);
        } else {
          doAdvancePlayer(players, deck, [...discard, drawnCard], maxSc);
        }
        return;
      }

      if (turnPhase === 'flip') {
        const anyFaceDown = cp.cards.map((c, i) => ({ ...c, idx: i })).filter(c => !c.faceUp);
        if (anyFaceDown.length > 0) {
          const target = anyFaceDown[Math.floor(Math.random() * anyFaceDown.length)];
          const newPlayers = players.map((pl, pli) =>
            pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === target.idx ? { ...c, faceUp: true } : c) }
          );
          doAdvancePlayer(newPlayers, deck, discard, maxSc);
        } else {
          doAdvancePlayer(players, deck, discard, maxSc);
        }
      }
    }, 1200);

    return () => { if (cpuTimer.current) clearTimeout(cpuTimer.current); };
  }, [phase, currentPlayer, turnPhase, drawnCard, gameEnding, doAdvancePlayer]);

  const cp = players[currentPlayer];
  const discardTop = (turnPhase === 'swap' || turnPhase === 'action') ? null : discard[discard.length - 1];
  const isHumanTurn = cp && !cp.isCPU;

  const setPeek = (key, val) => setPeekingCards(prev => {
    const n = new Set(prev);
    val ? n.add(key) : n.delete(key);
    return n;
  });

  // ── SETUP ─────────────────────────────────────────────────────────────────
  if (phase === 'setup') return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
      <div style={{ ...S.card, maxWidth: 440, width: '100%' }}>
        <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 4 }}>Four Play</div>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>A strategic card game for 2–6 players</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Number of players</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {[2,3,4,5,6].map(n => (
            <button key={n} onClick={() => setPlayerCount(n)} style={{
              padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
              border: playerCount === n ? '2px solid #1D9E75' : '1px solid #ddd',
              background: playerCount === n ? '#E1F5EE' : '#fff',
              color: playerCount === n ? '#0F6E56' : '#333',
              fontWeight: playerCount === n ? 500 : 400,
            }}>{n}</button>
          ))}
        </div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Player names</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {Array.from({ length: playerCount }, (_, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#888', minWidth: 20 }}>{i + 1}.</span>
              <input
                value={playerNames[i] || ''}
                onChange={e => setPlayerNames(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                placeholder={i === 0 ? 'Your name' : 'Enter name'}
                style={{ flex: 1, fontSize: 14, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8 }}
              />
             <button onClick={() => setPlayerNames(prev => {
             const n = [...prev];
              n[i] = n[i]?.includes('(Bot)') ? '' : BOT_NAMES[i % BOT_NAMES.length];
              return n;
              })} style={{
              padding: '6px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
              border: playerNames[i]?.includes('(Bot)') ? '2px solid #7F77DD' : '1px solid #ddd',
              background: playerNames[i]?.includes('(Bot)') ? '#EEEDFE' : '#fff',
              color: playerNames[i]?.includes('(Bot)') ? '#3C3489' : '#666',
              }} title="Add a computer player">
             {playerNames[i]?.includes('(Bot)') ? '🤖 Remove bot' : '+ Bot'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Max score to end game</div>
        <select value={maxScore} onChange={e => setMaxScore(Number(e.target.value))}
          style={{ width: '100%', marginBottom: 20, fontSize: 14, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8 }}>
          <option value={50}>50 points</option>
          <option value={100}>100 points</option>
          <option value={150}>150 points</option>
          <option value={200}>200 points</option>
        </select>
        <button onClick={startGame} style={{ ...S.btn, width: '100%' }}>Start game</button>
        {onOpenOnline && <button onClick={onOpenOnline} style={{ ...S.btn, width: '100%', marginTop: 10, background: '#7F77DD' }}>🌐 Play online</button>}
        {onOpenShop && <button onClick={onOpenShop} style={{ ...S.btnOutline, width: '100%', marginTop: 10 }}>Cosmetics Shop</button>}
        <button onClick={() => onOpenHowToPlay && onOpenHowToPlay()} style={{ ...S.btnOutline, width: '100%', marginTop: 10 }}>📋 How to play</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
        <span style={{ fontSize: 12, color: '#aaa' }}>Signed in as {profile?.displayName}</span>
        <button onClick={() => onSignOut && onSignOut()} style={{ background: 'none', border: 'none', fontSize: 12, color: '#E53E3E', cursor: 'pointer' }}>Sign out</button>
        </div>
        {errorMsg && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 300, width: '90%', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Not enough players</div>
              <div style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>You need at least 2 players to start. Add a player name or tap 🤖 Bot to add a computer opponent.</div>
              <button onClick={() => setErrorMsg('')} style={{ ...S.btn, width: '100%' }}>Got it</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── ROUND OVER ────────────────────────────────────────────────────────────
  if (phase === 'roundover') {
    const anyElim = roundResults.some(r => r.eliminated);
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        <div style={{ ...S.card, maxWidth: 480, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Round {round} results</div>
          {[...roundResults].sort((a, b) => a.roundScore - b.roundScore).map(r => {
            const playerData = players.find(p => p.name === r.name);
            const counts = {};
            playerData?.cards.forEach(c => { counts[c.value] = (counts[c.value] || 0) + 1; });
            return (
              <div key={r.name} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: r.isWinner ? '#0F6E56' : 'inherit' }}>
                    {r.isWinner ? '🏅 ' : ''}{r.name}
                  </span>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ color: '#888', fontSize: 13 }}>+{r.roundScore} pts</span>
                    <strong>{r.newTotal} total</strong>
                    {r.eliminated && <span style={{ fontSize: 11, background: '#FCEBEB', color: '#A32D2D', padding: '2px 8px', borderRadius: 6 }}>eliminated</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {playerData?.cards.map((card, ci) => {
                    const cancelled = counts[card.value] === 2 || counts[card.value] === 4;
                    return (
                      <div key={ci} style={{
                        width: 44, height: 62, borderRadius: 5, background: '#fff',
                        border: cancelled ? '2px solid #1D9E75' : '1.5px solid #e0e0e0',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', fontSize: 13, fontWeight: 500,
                        color: ['♥','♦'].includes(card.suit) ? '#D85A30' : '#1a1a1a',
                        position: 'relative', opacity: cancelled ? 0.6 : 1,
                      }}>
                        <div>{card.value}</div>
                        <div style={{ fontSize: '0.7em' }}>{card.suit}</div>
                        {cancelled && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#1D9E75', fontWeight: 700 }}>✕</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <button onClick={nextRound} style={{ ...S.btn, width: '100%', marginTop: 20 }}>
            {anyElim ? 'See final results' : 'Deal next round'}
          </button>
        </div>
      </div>
    );
  }

  // ── GAME OVER ─────────────────────────────────────────────────────────────
  if (phase === 'gameover') {
    const sorted = [...players].sort((a, b) => a.score - b.score);
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        <div style={{ ...S.card, maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Game over!</div>
          <div style={{ fontSize: 16, color: '#0F6E56', marginBottom: 24 }}>{sorted[0].name} wins with {sorted[0].score} points!</div>
          {sorted.map((p, i) => (
            <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
              <span>{['🥇','🥈','🥉'][i] || ''} {p.name}</span>
              <strong>{p.score} pts</strong>
            </div>
          ))}
          <button onClick={() => { setPhase('setup'); onGameStateChange && onGameStateChange(false); }} style={{ ...S.btn, width: '100%', marginTop: 20 }}>Play again</button>
          <button onClick={() => onOpenLeaderboard && onOpenLeaderboard()} style={{ ...S.btnOutline, width: '100%', marginTop: 10 }}>🏆 Leaderboard</button>
        </div>
      </div>
    );
  }

  // ── MAIN GAME ─────────────────────────────────────────────────────────────
  const cols = players.length === 2 ? 2 : players.length <= 4 ? 2 : players.length <= 6 ? 3 : 4;

  return (
    <div style={{ minHeight: '100vh', padding: '1rem', fontFamily: 'sans-serif', ...tableStyle }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#fff' }}>Four Play</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>Round {round}</div>
        </div>
     <div style={{ position: 'relative' }}>
          <button onClick={e => { e.stopPropagation(); setShowMenu(prev => !prev); }} style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#1D9E75', border: '2px solid rgba(255,255,255,0.4)',
            color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {profile?.displayName?.charAt(0)?.toUpperCase() || '?'}
          </button>
          {showMenu && (
            <div style={{
              position: 'absolute', top: 44, right: 0, background: '#fff',
              borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 180, zIndex: 200, overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#f9f9f9' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>{profile?.displayName || 'Player'}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{user?.isAnonymous ? 'Guest account' : user?.email || ''}</div>
              </div>
              {[
                { icon: '🎮', label: 'New game', action: () => { setPhase('setup'); onGameStateChange && onGameStateChange(false); setShowMenu(false); } },
                { icon: '🌐', label: 'Play online', action: () => { onOpenOnline && onOpenOnline(); setShowMenu(false); } },
                { icon: '🛍️', label: 'Shop', action: () => { onOpenShop && onOpenShop(); setShowMenu(false); } },
                { icon: '🏆', label: 'Leaderboard', action: () => { onOpenLeaderboard && onOpenLeaderboard(); setShowMenu(false); } },
                { icon: '📊', label: 'Stats', action: () => { setShowStats(true); setShowMenu(false); } },
                { icon: '📋', label: 'How to play', action: () => { onOpenHowToPlay && onOpenHowToPlay(); setShowMenu(false); } },
              ].map(item => (
                <button key={item.label} onClick={item.action} style={{
                  width: '100%', padding: '11px 16px', background: 'none', border: 'none',
                  borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#1a1a1a',
                }}>
                  <span>{item.icon}</span>{item.label}
                </button>
              ))}
              <button onClick={() => { onSignOut && onSignOut(); setShowMenu(false); }} style={{
                width: '100%', padding: '11px 16px', background: 'none', border: 'none',
                cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center',
                gap: 10, fontSize: 14, color: '#E53E3E',
              }}>
                <span>🚪</span> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, color: '#fff', textAlign: 'center' }}>
        {gameEnding && <span>⏱ Round ending...</span>}
        {!gameEnding && cp?.isCPU && <span>🤖 <strong>{cp?.name}</strong> is thinking...</span>}
        {!gameEnding && isHumanTurn && turnPhase === 'draw' && !drawnCard && discard.length > 0 && <span><strong>{cp?.name}'s turn</strong> — draw from pile, or tap discard to take it</span>}
        {!gameEnding && isHumanTurn && turnPhase === 'draw' && !drawnCard && discard.length === 0 && <span><strong>{cp?.name}'s turn</strong> — draw a card from the pile</span>}
        {!gameEnding && isHumanTurn && turnPhase === 'action' && <span><strong>{cp?.name}</strong> — tap a face-down card to flip it, or tap drawn card to swap it in</span>}
        {!gameEnding && isHumanTurn && turnPhase === 'swap' && <span><strong>{cp?.name}</strong> — tap one of your face-down cards to replace it</span>}
        {!gameEnding && isHumanTurn && turnPhase === 'flip' && <span><strong>{cp?.name}</strong> — tap one of your face-down cards to flip it</span>}
      </div>

      <div style={{ display: 'flex', gap: 20, justifyContent: 'center', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Draw pile</div>
          <CardBack skinId={skinId} onClick={isHumanTurn && turnPhase === 'draw' && !gameEnding ? drawFromDeck : undefined} glow={isHumanTurn && turnPhase === 'draw' && !gameEnding} />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{deck.length} left</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Drawn</div>
          {drawnCard
            ? <>
                <CardFace card={drawnCard} highlight onClick={isHumanTurn && turnPhase === 'action' ? () => setTurnPhase('swap') : undefined} />
                {isHumanTurn && turnPhase === 'action' && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>tap to swap in</div>}
              </>
            : <div style={{ width: 52, height: 74, borderRadius: 6, border: '1px dashed rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>—</span>
              </div>
          }
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Discard</div>
          {discardTop
            ? <>
                <CardFace card={discardTop} highlight={isHumanTurn && turnPhase === 'draw' && !drawnCard && !gameEnding} onClick={isHumanTurn && turnPhase === 'draw' && !drawnCard && !gameEnding ? takeDiscard : undefined} />
                {isHumanTurn && turnPhase === 'draw' && !drawnCard && !gameEnding && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>tap to take</div>}
              </>
            : <div style={{ width: 52, height: 74, borderRadius: 6, border: '1px dashed rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>—</span>
              </div>
          }
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {isHumanTurn && !gameEnding && (turnPhase === 'action' || turnPhase === 'swap') && drawnCard && (
          <button onClick={discardDrawn} style={S.btnWhite}>
            {turnPhase === 'swap' ? 'Cancel — discard drawn card' : 'Discard drawn card & flip one of mine'}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cols, players.length)}, 1fr)`, gap: 10 }}>
        {players.map((p, pi) => {
          const isActive = pi === currentPlayer && !gameEnding;
          return (
            <div key={pi} style={{
              background: 'rgba(255,255,255,0.95)', borderRadius: 12, padding: 12,
              border: isActive ? '2px solid #1D9E75' : '2px solid transparent',
              boxShadow: isActive ? '0 0 0 3px rgba(29,158,117,0.2)' : 'none',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {isActive && <span style={{ fontSize: 11, background: p.isCPU ? '#EEEDFE' : '#E1F5EE', color: p.isCPU ? '#3C3489' : '#0F6E56', padding: '2px 8px', borderRadius: 6 }}>{p.isCPU ? '🤖 thinking' : 'your turn'}</span>}
                  <span style={{ fontSize: 12, color: '#888', background: '#f5f5f5', padding: '2px 8px', borderRadius: 6 }}>Total: {p.score}</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, width: 'fit-content', margin: '0 auto' }}>
                {p.cards.map((card, ci) => {
                  const isBottomTwo = ci >= 2;
                  const peeking = peekingCards.has(`${pi}-${ci}`);
                  const canFlipForDiscard = isActive && !p.isCPU && drawnCard && turnPhase === 'action' && !card.faceUp;
                  const canSwapIn = isActive && !p.isCPU && drawnCard && turnPhase === 'swap' && !card.faceUp;
                  const canFlipBlind = isActive && !p.isCPU && turnPhase === 'flip' && !card.faceUp;
                  const glow = canFlipForDiscard || canSwapIn || canFlipBlind;

                  const handleClick = canFlipForDiscard ? () => flipCardDiscardDrawn(pi, ci)
                    : canSwapIn ? () => swapDrawnIntoCard(pi, ci)
                    : canFlipBlind ? () => flipBlindCard(pi, ci)
                    : undefined;

                  if (card.faceUp) return <CardFace key={ci} card={card} />;

                  if (isBottomTwo && isActive && !p.isCPU) {
                    return (
                      <div key={ci} style={{ position: 'relative', paddingBottom: 26 }}>
                        {peeking
                          ? <CardFace card={card} onClick={handleClick} />
                          : <CardBack skinId={skinId} glow={glow} onClick={handleClick} />
                        }
                        <button
                          onMouseDown={() => setPeek(`${pi}-${ci}`, true)}
                          onMouseUp={() => setPeek(`${pi}-${ci}`, false)}
                          onMouseLeave={() => setPeek(`${pi}-${ci}`, false)}
                          onTouchStart={e => { e.preventDefault(); setPeek(`${pi}-${ci}`, true); }}
                          onTouchEnd={() => setPeek(`${pi}-${ci}`, false)}
                          style={{
                            position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
                            background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 4,
                            fontSize: 10, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap',
                          }}>👁 peek</button>
                      </div>
                    );
                  }

                  return <CardBack key={ci} skinId={skinId} glow={glow} onClick={handleClick} />;
                })}
              </div>
            </div>
          );
        })}
      </div>

      {showStats && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 340, width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Your Stats</div>
              <button onClick={() => setShowStats(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Games played', value: profile?.stats?.gamesPlayed || 0 },
                { label: 'vs Human wins', value: profile?.stats?.wins || 0 },
                { label: 'vs Bot wins', value: profile?.stats?.botWins || 0 },
                { label: 'Rounds played', value: profile?.stats?.totalRounds || 0 },
                { label: 'Best round', value: profile?.stats?.bestRoundScore !== null && profile?.stats?.bestRoundScore !== undefined ? `${profile.stats.bestRoundScore} pts` : '—' },
                { label: 'Human win rate', value: (profile?.stats?.wins > 0 && profile?.stats?.gamesPlayed > 0) ? `${profile.stats.wins}/${profile.stats.gamesPlayed} (${Math.round((profile.stats.wins / profile.stats.gamesPlayed) * 100)}%)` : '—' },
              ].map(s => (
                <div key={s.label} style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 500, color: '#1D9E75' }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowStats(false)} style={{ ...S.btn, width: '100%' }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
